import { useState, useEffect, useCallback, useRef } from "react";
import { api, isConnectionError } from "@/lib/api";

// Loads one Core resource; no caching so account switches never show stale data.
export function useCore(path, select) {
  const [state, setState] = useState({ data: null, error: null });
  const requestId = useRef(0);
  const selectRef = useRef(select);
  selectRef.current = select;

  const load = useCallback(() => {
    const id = ++requestId.current;
    if (!path) { setState({ data: null, error: null }); return; }
    setState({ data: null, error: null });
    api.get(path)
      .then((r) => {
        if (id !== requestId.current) return;
        setState({ data: selectRef.current ? selectRef.current(r.data) : r.data, error: null });
      })
      .catch((e) => {
        if (id !== requestId.current) return;
        const s = !isConnectionError(e) && e.response?.status;
        setState({ data: null, error: s === 404 ? "notfound" : s === 403 ? "forbidden" : s === 410 ? "gone" : "connection" });
      });
  }, [path]);

  useEffect(() => {
    load();
    return () => { requestId.current += 1; };
  }, [load]);
  return { ...state, reload: load };
}

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}
